import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import BuyerChecklistFull from "../../components/automap/buyerChecklist/BuyerChecklistFull";
import VehicleChecklistContextCard from "../../components/automap/buyerChecklist/VehicleChecklistContextCard";
import SeoBreadcrumbs from "../../components/automap/seo/SeoBreadcrumbs";
import AutoMapaHomeNav from "../../clientViewRuntime/components/AutoMapaHomeNav";
import { useClientView } from "../../clientViewRuntime/context/ClientViewProvider";
import { ClientViewRuntimeProviders } from "../../clientViewRuntime/context/ClientViewRuntimeProviders";
import { useClientViewSeo } from "../../clientViewRuntime/hooks/useClientViewSeo";
import { useLocalizedRuntimePaths } from "../../clientViewRuntime/hooks/useLocalizedRuntimePaths";
import { useLocale } from "../../intl";
import { getBuyerChecklistHubCopy } from "../../intl/automotiveCopy";
import {
  getLocalizedChecklistSeo,
  getLocalizedChecklistSummaryFallback,
} from "../../intl/buyerChecklistCopy";
import {
  CHECKLIST_ANALYTICS_EVENTS,
  trackBuyerChecklistEvent,
} from "../../constants/buyerChecklistConstants";
import { getVehicleListingDetail } from "../../services/vehicleDetailService";
import { normalizeVehicleListing } from "../../utils/automapListing";
import {
  buildVehicleDetailChecklist,
  listingFromChecklistQueryParams,
} from "../../utils/buyerChecklistViewModel";
import "../../styles/autoclaroHome.css";
import "../../styles/buyerChecklist.css";

export function BuyerChecklistHubPageContent() {
  const { manifest } = useClientView();
  const siteName = manifest?.name || "AutoMapa";
  const { t, exists } = useLocale();
  const copy = useMemo(() => getBuyerChecklistHubCopy(t), [t]);
  const { resolveCtaHref, vehicleSearchPath, homePath, vehicleDetailPath } = useLocalizedRuntimePaths();
  const [searchParams] = useSearchParams();
  const listingId = searchParams.get("listingId");
  const hasVehicleQueryContext = Boolean(
    listingId ||
      searchParams.get("make") ||
      searchParams.get("model") ||
      searchParams.get("year")
  );
  const [listing, setListing] = useState(() => listingFromChecklistQueryParams(searchParams));
  const [loadError, setLoadError] = useState(false);

  const guideHref = resolveCtaHref("/guias/checklist-compra-carro-usado");
  const savedProgressHref = resolveCtaHref("/mi-checklist");

  const checklistSeo = useMemo(
    () => getLocalizedChecklistSeo(t, exists, { hasVehicleQueryContext }),
    [t, exists, hasVehicleQueryContext]
  );

  const breadcrumbs = useMemo(
    () => [
      { label: t("automotive.dealerDirectory.breadcrumbHome"), href: homePath },
      { label: copy.eyebrow, href: resolveCtaHref("/checklist-compra") },
    ],
    [t, copy.eyebrow, homePath, resolveCtaHref]
  );

  useClientViewSeo({
    title: checklistSeo.title,
    description: checklistSeo.description,
    canonicalUrl: checklistSeo.canonicalUrl,
    robots: checklistSeo.robots,
    openGraph: checklistSeo.openGraph,
    twitter: checklistSeo.twitter,
    structuredData: checklistSeo.structuredData,
    jsonLdId: "automap-checklist-jsonld",
  });

  useEffect(() => {
    trackBuyerChecklistEvent(CHECKLIST_ANALYTICS_EVENTS.FULL_OPENED, {
      page: "hub",
      listingId,
      sourcePage: "checklist_compra",
    });
  }, [listingId]);

  useEffect(() => {
    const fromParams = listingFromChecklistQueryParams(searchParams);
    setListing(fromParams);
    setLoadError(false);

    if (!listingId) return undefined;

    let cancelled = false;
    getVehicleListingDetail("automap", listingId)
      .then((result) => {
        if (cancelled) return;
        if (result?.listing) {
          setListing(normalizeVehicleListing(result.listing));
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [listingId, searchParams]);

  const checklistVm = useMemo(() => {
    const vm = buildVehicleDetailChecklist(listing, { listingId });
    const fallbackSummary = getLocalizedChecklistSummaryFallback(t, exists);
    if (!vm.checklistSummary || vm.checklistSummary === "Checklist general para comprar carro usado") {
      return { ...vm, checklistSummary: fallbackSummary };
    }
    return vm;
  }, [listing, listingId, t, exists]);
  const contextualItemIds = useMemo(
    () => new Set(checklistVm.allItems.map((item) => item.id)),
    [checklistVm.allItems]
  );
  const detailHref = listing?.id ? vehicleDetailPath(listing.slug || listing.id) : null;

  return (
    <div className="autoclaro-home buyer-checklist-marketplace">
      <AutoMapaHomeNav siteName={siteName} />

      <div className="buyer-checklist-page">
        <header className="buyer-checklist-page__hero">
          <div className="wrap">
            <SeoBreadcrumbs items={breadcrumbs} />
            <span className="buyer-checklist-page__eyebrow">{copy.eyebrow}</span>
            <h1>{copy.title}</h1>
            <p>{copy.intro}</p>
          </div>
        </header>

        <main className="buyer-checklist-page__main wrap">
          {listing ? (
            <VehicleChecklistContextCard
              listing={listing}
              checklistVm={checklistVm}
              detailHref={detailHref}
              copy={copy}
            />
          ) : null}

          {loadError ? (
            <p className="buyer-checklist-page__notice" role="status">
              {copy.loadError}
            </p>
          ) : null}

          <BuyerChecklistFull
            listingId={listingId}
            defaultVehicleContext={listing}
            showImportance
            highlightItemIds={contextualItemIds}
            disclaimer={copy.disclaimer}
            actionCopy={copy}
          />

          <footer className="buyer-checklist-page__footer">
            <p className="buyer-checklist-page__footer-note">{copy.footerNote}</p>
            <div className="buyer-checklist-page__cta-row">
              <Link to={guideHref} className="btn btn-soft buyer-checklist-page__cta">
                {copy.viewDetailedGuide}
              </Link>
              <Link to={vehicleSearchPath} className="btn btn-primary buyer-checklist-page__cta">
                {copy.searchVehicles}
              </Link>
              <Link to={savedProgressHref} className="btn btn-blue buyer-checklist-page__cta">
                {copy.savedProgress}
              </Link>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}

export default function BuyerChecklistHubPage() {
  return (
    <ClientViewRuntimeProviders slug="automap">
      <BuyerChecklistHubPageContent />
    </ClientViewRuntimeProviders>
  );
}
